import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Star, Clock, Shield, Users, Play } from 'lucide-react';

const CTASection = () => {
  return (
    <section className="py-20 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-grid-white/[0.05] [mask-image:linear-gradient(0deg,transparent,black,transparent)]" />
      
      <div className="container mx-auto px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="text-center max-w-4xl mx-auto"
        >
          {/* Urgency Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-8"
          >
            <Badge className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 text-lg font-bold">
              <Clock className="h-5 w-5 mr-2" />
              Early Bird Pricing Ends Soon!
            </Badge>
          </motion.div>

          {/* Main Headline */}
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight"
          >
            Ready to Transform How You
            <br />
            <span className="bg-gradient-to-r from-yellow-300 to-yellow-400 bg-clip-text text-transparent">
              Share Videos?
            </span>
          </motion.h2>

          {/* Sub-headline */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-xl md:text-2xl text-blue-100 mb-8 leading-relaxed"
          >
            Join activity centers already using HiveShare to delight parents
            <br />
            and save hours every week.
          </motion.p>

          {/* Trust Indicators */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="flex flex-wrap items-center justify-center gap-8 mb-12 text-blue-200"
          >
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              <span>Enterprise Security</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              <span>Unlimited Families</span>
            </div>
            <div className="flex items-center gap-2">
              <Play className="h-5 w-5" />
              <span>No Ads Ever</span>
            </div>
            <div className="flex items-center gap-2">
              <Star className="h-5 w-5 fill-current" />
              <span>5-Star Support</span>
            </div>
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8"
          >
            <Button
              size="lg"
              className="bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-gray-900 px-8 py-4 text-xl font-bold shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-105"
            >
              Start Your Free Trial
              <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
            
            <Button
              variant="outline"
              size="lg"
              className="border-2 border-white text-white hover:bg-white hover:text-blue-600 px-8 py-4 text-xl font-bold bg-transparent"
            >
              <Play className="h-5 w-5 mr-2" />
              Book 10-Minute Demo
            </Button>
          </motion.div>

          {/* Final Reassurance */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="text-blue-200 space-y-2"
          >
            <p className="text-lg">
              ✨ Set up in under 5 minutes • 💳 No credit card required
            </p>
            <p className="text-lg">
              🔒 Cancel anytime • 📞 Phone support included
            </p>
          </motion.div>
        </motion.div>

        {/* Social Proof Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.9 }}
          className="mt-20 max-w-5xl mx-auto"
        >
          <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl p-8 border border-white border-opacity-20">
            <h3 className="text-2xl font-bold text-white text-center mb-8">
              Join These Happy Activity Center Owners
            </h3>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white bg-opacity-20 rounded-xl p-6 backdrop-blur-sm">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-white mb-4 italic">
                  "HiveShare transformed how we connect with parents. The iOS app is a game-changer!"
                </p>
                <div>
                  <p className="font-semibold text-white">Jennifer K.</p>
                  <p className="text-blue-200 text-sm">Elite Dance Academy</p>
                </div>
              </div>
              
              <div className="bg-white bg-opacity-20 rounded-xl p-6 backdrop-blur-sm">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-white mb-4 italic">
                  "Finally, secure video sharing built for kids programs. Parents love it!"
                </p>
                <div>
                  <p className="font-semibold text-white">Coach Mike R.</p>
                  <p className="text-blue-200 text-sm">Champions Soccer Club</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Final Urgency Reminder */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 1.1 }}
          className="text-center mt-12"
        >
          <div className="bg-red-600 bg-opacity-90 rounded-2xl p-6 max-w-2xl mx-auto border border-red-400">
            <Clock className="h-8 w-8 text-white mx-auto mb-3" />
            <p className="text-white font-bold text-lg mb-2">
              🚨 Only 3 Early Bird Spots Remaining
            </p>
            <p className="text-red-100 text-sm">
              Lock in $29/month pricing before it increases to $49/month at launch
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CTASection;